
    import Vue from "vue";
    import Component from "vue-class-component";
    import { StateChanger } from "vue-infinite-loading";
    import { Watch } from "vue-property-decorator";
    import { Validations } from "vuelidate-property-decorators";
    import { required, minValue } from "vuelidate/lib/validators";
    import ApiButton from "@/components/ApiButton.vue";
    import DatePicker from "@/components/DatePicker.vue";
    import { BannerMessage, IBannerMessage } from "@/model/BannerMessage";
    import { UserRole } from "@/model/Enums";
    import apiClient from "@/stuff/ApiClient";
    import utils from "@/stuff/Utils";
    import * as toastr from "toastr";

    @Component({ components: { ApiButton, DatePicker } })
    export default class BannerMessages extends Vue {

        //
        // -- properties
        //

        messagePage: number = 1;
        messageCount: number = -1;
        messages: Array<BannerMessage> = [];

        readonly message: BannerMessage = new BannerMessage();

        //
        // computed
        //

        get role(): UserRole {
            return this.$route.params.target as unknown as UserRole;
        }
        get dialogueTitle(): string {
            return this.message.isNew ? "Add Banner Message" : "Edit Banner Message";
        }

        //
        // -- watchers
        //

        @Watch("role")
        onRoleChanged(): void {
            this.resetSearch();
        }

        //
        // -- methods
        //

        mounted(): void {
            this.search();
        }

        async resetSearch(event?: Event): Promise<void> {
            this.messages = [];
            this.messagePage = 1;
            this.messageCount = -1;
            this.search(event)
        }

        async infiniteLoadingHandler(stateChanger: StateChanger): Promise<void> {
            if (this.messages.length >= this.messageCount && this.messageCount > -1) {
                stateChanger.complete();
                return;
            }

            this.messagePage += 1;

            await this.search();

            if (this.messages.length >= this.messageCount) {
                stateChanger.complete();
            }
            else {
                stateChanger.loaded();
            }
        }

        async search(event?: Event): Promise<void> {
            const response: { count: number, list: Array<IBannerMessage> } = await apiClient.get(`api/message/${this.role.toString()}/search/${this.messagePage}`, event);
            if (this.messagePage == 1) this.messageCount = response.count;
            this.messages.push(...response.list.map(m => new BannerMessage(m)));
        }

        async edit(message: IBannerMessage): Promise<void> {
            utils.resetObject(this.message);
            const response: IBannerMessage = await apiClient.get(`api/message/${message.id}/load`);
            this.message.update(response);
            this.$v.$reset();
            this.$bvModal.show("messageDialogue");
        }

        editNew(): void {
            utils.resetObject(this.message);
            this.$v.$reset();
            this.message.supplier = this.role.toString() == "Supplier";
            this.message.buyer = this.role.toString() == "Buyer";
            this.$bvModal.show("messageDialogue");
        }

        async saveMessage(event?: Event): Promise<void> {
            this.$v.$touch();
            if (this.$v.message.$invalid) {
                toastr.info("Please fix the highlighted errors", "Validation errors");
                return;
            }

            const response: { message: string } = await apiClient.post("api/message/save", this.message, event);
            if (response.message == "ok") {
                toastr.success("Saved")
                this.$bvModal.hide("messageDialogue");
                await this.resetSearch();
            }
            else {
                toastr.warning(response.message);
            }
        }

        cancelMessage(): void {
            this.$bvModal.hide("messageDialogue");
            utils.resetObject(this.message);
        }

        async deleteMessage(event?: Event): Promise<void> {
            if (this.message.isNew) return;

            const shouldDelete: boolean = await this.$bvModal.msgBoxConfirm("Are you sure you want to delete this message?", {
                title: "Delete Message",
                okVariant: "danger",
                okTitle: "Yes, delete!",
                cancelTitle: "No, leave it",
                hideHeaderClose: true,
                centered: true,
                headerClass: "border-bottom-0",
                footerClass: "border-top-0",
                size: "sm"
            });

            if (!shouldDelete) return;

            const response: { message: string } = await apiClient.get(`api/message/${this.message.id}/delete`, event);
            if (response.message == "ok") {
                toastr.warning("Deleted")
                this.$bvModal.hide("messageDialogue");
                await this.resetSearch();
            }
            else {
                toastr.warning(response.message);
            }
        }

        //
        // -- validations
        //

        @Validations()
        validations() {
            const validations = {
                message: {} as any // eslint-disable-line @typescript-eslint/no-explicit-any
            }

            validations.message.message = { required };
            validations.message.availableFrom = { required };

            return validations;
        }

    }
